import Vue from 'vue'
import { mapActions, mapGetters, mapState } from 'vuex'
import BigNumber from 'bignumber.js'
import { BookmarkTypes } from '@/constants/bookmarks/bookmarkType'
import { RoomTypes } from '@/constants/chat/RoomTypes'
import { BUTTON_TRANSPARENT_MEDIUM } from '@/constants/components/button'
import { BROWSE_VACANCIES_BY_ROLE, BROWSE_VACANCIES_BY_SKILL, CUSTOMER_PROFILE, VACANCY_CHAT, VACANCY_DETAILS } from '@/constants/routes'
import { POSITION_TYPES, PositionType } from '@/constants/vacancies/positionTypes'
import { SalaryTypes } from '@/constants/vacancies/SalaryTypes'
import rolebleMixin from '@/mixins/rolebleMixin'
import ChatRoom from '@/models-ts/chat/ChatRoom'
import Skill from '@/models-ts/Skill'
import VacancyApplication from '@/models-ts/vacancies/VacancyApplication'
import BookmarkIconButton from '@/partials/BookmarkButtons/BookmarkIconButton/BookmarkIconButton.vue'
import UserInfo from '@/partials/UserInfo/UserInfo.vue'
import VacancyLocationTooltip from '@/partials/VacancyLocationTooltip/VacancyLocationTooltip.vue'
import { formatCardsDescription } from '@/utils-ts/strings'
import { getHumanDate } from '@/utils/date'
import { formatUsd } from '@/utils/moneyFormat'

export default Vue.extend<any, any, any, any>({
  mixins: [rolebleMixin],
  components: {
    BookmarkIconButton,
    UserInfo,
    VacancyLocationTooltip,
  },
  props: {
    application: VacancyApplication,
  },
  data() {
    return {
      BUTTON_TRANSPARENT_MEDIUM,
      CUSTOMER_PROFILE,
      VACANCY_DETAILS,
      bookmarkType: BookmarkTypes.VACANCY,
    }
  },
  computed: {
    ...mapGetters({
      getUnreadCount: 'chatNew/getUnreadCount',
    }),
    customerType() {
      return this.application.customer.customerType
    },
    vacancy() {
      return this.application.vacancy
    },
    description() {
      return formatCardsDescription(this.vacancy.description)
    },
    user() {
      return this.isFreelancer ? this.application.customer : this.application.freelancer
    },
    createdAt() {
      const date = this.vacancy.published_at || this.vacancy.updated_at
      return getHumanDate(date)
    },
    userId() {
      return this.vacancy.user?.id
    },
    userAvatar() {
      return this.vacancy.user?.avatar
    },
    userType() {
      return this.vacancy.user?.type
    },
    userName() {
      return this.vacancy.user?.name
    },
    positionType() {
      return POSITION_TYPES[this.vacancy.positionType as PositionType]?.name
    },
    isRangeSalary() {
      return this.vacancy.salary_type === SalaryTypes.RANGE
    },
    roleTag() {
      if (this.vacancy.primaryRole?.id) {
        return {
          text: this.vacancy.primaryRole.name,
          link: { name: BROWSE_VACANCIES_BY_ROLE, params: { role: this.vacancy.primaryRole.url } },
        }
      }
    },
    skills() {
      const skills = (this.vacancy.skills || []).map((skill: Skill) => ({
        text: skill.name,
        link: {
          name: BROWSE_VACANCIES_BY_SKILL,
          params: { skill: skill.url },
        },
      }))
      const tags = []
      if (this.roleTag) {
        tags.push(this.roleTag)
      }
      return [...tags, ...skills].slice(0, 2)
    },
    moreSkills() {
      if (this.skills.length > 1) {
        return this.skills.length - 1
      }
    },
    salaryFrom() {
      const salaryFrom = new BigNumber(this.vacancy.salary_from).div(12)
      return salaryFrom.gt(100) ? formatUsd(salaryFrom, 0) : formatUsd(salaryFrom, 2)
    },
    salaryTo() {
      const salaryTo = new BigNumber(this.vacancy.salary_to).div(12)
      return salaryTo.gt(100) ? formatUsd(salaryTo, 0) : formatUsd(salaryTo, 2)
    },
    remoteInfo() {
      return this.application.remoteInfo
    },
    chatRoomId() {
      return ChatRoom.generateRoomId({
        type: RoomTypes.VACANCY,
        applicationId: this.application.id,
        freelancerId: this.application.freelancer.id,
        customerId: this.application.customer.id,
      })
    },
    chatLink() {
      return {
        name: VACANCY_CHAT,
        params: { id: this.vacancy.id, slug: this.vacancy.slug },
        query: { room: this.chatRoomId },
      }
    },
    hasNewMsgs() {
      const count: number = this.getUnreadCount(this.chatRoomId)
      return Boolean(count)
    },
  },
  methods: {
    ...mapActions({
      openModal: 'ui/openModal',
    }),
    formatUsd,
    onChatClick() {
      this.$router.push(this.chatLink)
    },
  },
})
